import { useEffect, useState, useContext } from 'react';
import { Box, CircularProgress, Typography, keyframes } from '@mui/material';
import { GameContext } from '../../../contexts/GameContext';
import { TurnStateEnum } from '../../../Enums/TurnStateEnum';
import { useTranslation } from '../../../contexts/LanguageContext';
import { AlertContext } from '../../../contexts/AlertContext';

const backdropFade = keyframes`
    0% { opacity: 0; }
    100% { opacity: 1; }
`;

const cardDrop = keyframes`
    0%   { opacity: 0; transform: translate(-50%, -120%) scale(0.6) rotate(-15deg); }
    55%  { opacity: 1; transform: translate(-50%, -42%)  scale(1.08) rotate(6deg); }
    70%  { transform: translate(-50%, -52%) scale(0.98) rotate(-4deg); }
    85%  { transform: translate(-50%, -48%) scale(1.02) rotate(2deg); }
    100% { opacity: 1; transform: translate(-50%, -50%) scale(1)    rotate(0deg); }
`;

const cardShake = keyframes`
    0%, 100% { transform: translate(-50%, -50%) rotate(0deg); }
    20%      { transform: translate(-52%, -50%) rotate(-2deg); }
    40%      { transform: translate(-48%, -50%) rotate(2deg); }
    60%      { transform: translate(-51%, -50%) rotate(-1deg); }
    80%      { transform: translate(-49%, -50%) rotate(1deg); }
`;

const heartBreak = keyframes`
    0%   { transform: scale(0) rotate(0deg); opacity: 0; }
    40%  { transform: scale(1.4) rotate(-8deg); opacity: 1; }
    60%  { transform: scale(0.95) rotate(6deg); }
    80%  { transform: scale(1.1) rotate(-4deg); }
    100% { transform: scale(1) rotate(0deg); opacity: 1; }
`;

const heartFloat = keyframes`
    0%, 100% { transform: translateY(0) rotate(0deg); }
    50%      { transform: translateY(-8px) rotate(-6deg); }
`;

const fadeInUp = keyframes`
    0%   { opacity: 0; transform: translate(-50%, 20px); }
    100% { opacity: 1; transform: translate(-50%, 0); }
`;

const messageGlow = keyframes`
    0%, 100% { text-shadow: 0 0 15px rgba(255, 80, 120, 0.55), 0 0 30px rgba(193, 145, 255, 0.3); }
    50%      { text-shadow: 0 0 28px rgba(255, 80, 120, 0.9),  0 0 55px rgba(193, 145, 255, 0.55); }
`;

const hintPulse = keyframes`
    0%, 100% { opacity: 0.65; transform: translateX(-50%) scale(1); }
    50%      { opacity: 1;    transform: translateX(-50%) scale(1.05); }
`;

const NoWinnerOverlay = () => {
    const gameContext = useContext(GameContext);
    const alertContext = useContext(AlertContext);
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);

    const turn = gameContext.currentTurn;

    useEffect(() => {
        setLoading(false);
    }, [turn?.turnCount, turn?.turnState]);

    if (!turn) return null;
    if (turn.turnState !== TurnStateEnum.turnFinished) return null;

    const track = turn.track;
    const isTurnHolder = gameContext.isPlayerTurn();
    const hasWon = gameContext.hasPlayerWin();
    const resultMessage = hasWon
        ? t('youWon')
        : turn.winnerId
            ? t('winnerIs', { name: gameContext.getWinnerName(turn.winnerId) })
            : t('nobodyWon');

    const handleCardClick = async () => {
        if (!isTurnHolder || loading) return;
        setLoading(true);
        const result = await gameContext.getClient().CreateNewTurn();
        if (!result.success) {
            alertContext?.setAlertMessage(result.errorMessage);
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                pointerEvents: 'none',
                zIndex: 1300,
                backgroundColor: 'rgba(10, 14, 30, 0.72)',
                backdropFilter: 'blur(2px)',
                animation: `${backdropFade} 0.4s ease-out`,
            }}
        >
            <Box
                onClick={handleCardClick}
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    width: 320,
                    height: 320,
                    backgroundImage: 'url(/images/cards/gradient.png)',
                    backgroundSize: '3000px 1000px',
                    backgroundRepeat: 'repeat',
                    border: '2px solid rgba(255, 107, 157, 0.7)',
                    borderRadius: 2,
                    boxShadow:
                        '0 0 50px rgba(255, 80, 120, 0.55), 0 0 100px rgba(193, 145, 255, 0.3), inset 0 0 30px rgba(0, 0, 0, 0.4)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: 3,
                    boxSizing: 'border-box',
                    transform: 'translate(-50%, -50%)',
                    pointerEvents: isTurnHolder ? 'auto' : 'none',
                    cursor: isTurnHolder ? 'pointer' : 'default',
                    transition: 'box-shadow 0.25s ease, border-color 0.25s ease',
                    animation: `${cardDrop} 0.9s cubic-bezier(0.34, 1.56, 0.64, 1) both, ${cardShake} 0.6s ease-in-out 0.95s 2`,
                    '&:hover': isTurnHolder
                        ? {
                              borderColor: 'rgba(255, 200, 220, 0.95)',
                              boxShadow:
                                  '0 0 70px rgba(255, 80, 120, 0.8), 0 0 140px rgba(193, 145, 255, 0.45), inset 0 0 30px rgba(0, 0, 0, 0.4)',
                          }
                        : {},
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: -80,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        fontSize: '4.5rem',
                        animation: `${heartBreak} 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) 0.5s both, ${heartFloat} 2.4s ease-in-out 1.4s infinite`,
                        filter: 'drop-shadow(0 0 18px rgba(255, 80, 120, 0.7))',
                        pointerEvents: 'none',
                    }}
                >
                    {hasWon ? '❤️' : '💔'}
                </Box>

                <Typography
                    variant="body1"
                    sx={{
                        color: 'white',
                        textShadow: '0 0 8px rgba(193, 145, 255, 0.4)',
                        textAlign: 'center',
                        mb: 2,
                    }}
                >
                    {track.artist}
                </Typography>
                <Typography
                    variant="h2"
                    sx={{
                        color: '#fff',
                        textAlign: 'center',
                        mb: 2,
                        textShadow: '0 0 20px rgba(255, 107, 157, 0.5)',
                    }}
                >
                    {track.year}
                </Typography>
                <Typography
                    variant="body1"
                    sx={{
                        color: '#fff',
                        textShadow: '0 0 8px rgba(193, 145, 255, 0.4)',
                        textAlign: 'center',
                    }}
                >
                    {track.title}
                </Typography>

                {loading && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'rgba(10, 14, 30, 0.55)',
                            borderRadius: 2,
                        }}
                    >
                        <CircularProgress sx={{ color: 'white' }} />
                    </Box>
                )}
            </Box>

            <Typography
                variant="h5"
                sx={{
                    position: 'absolute',
                    top: 'calc(50% + 200px)',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    color: 'white',
                    whiteSpace: 'nowrap',
                    fontWeight: 'bold',
                    animation: `${fadeInUp} 0.6s ease-out 0.9s both, ${messageGlow} 2.2s ease-in-out 1.5s infinite`,
                }}
            >
                {resultMessage}
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    position: 'absolute',
                    top: 'calc(50% + 250px)',
                    left: '50%',
                    color: 'rgba(255, 255, 255, 0.85)',
                    whiteSpace: 'nowrap',
                    letterSpacing: '0.5px',
                    transform: 'translateX(-50%)',
                    animation: `${hintPulse} 1.6s ease-in-out 2s infinite`,
                }}
            >
                {isTurnHolder
                    ? `👆 ${t('clickCardToContinue')}`
                    : t('waitingForTurnHolder', { name: turn.turnHolder.playerName })}
            </Typography>
        </Box>
    );
};

export default NoWinnerOverlay;
